/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — ConditionRow sub-primitive
 *
 * A single condition row: optional field picker + operator picker + value input
 * + remove button. When `fields` is provided the first column becomes a DS Select
 * that lets the user pick which field to condition on; changing the field clears
 * the operator and value since they are field-specific. The `renderFieldSelect`
 * escape hatch replaces that default Select when provided.
 */
import cx from 'classnames';
import React from 'react';

import IconButton from '../../components/IconButton/IconButton';
import Select from '../../components/Select/Select';
import TagInput from '../../components/TagInput/TagInput';
import TextInput from '../../components/TextInput/TextInput';
import type {
  FieldSelectRenderProps,
  SelectOptionData,
  ValidationCondition,
  ValidationField,
  ValidationOperator,
  ValueInputRenderProps,
} from '../../types';

import './ConditionRow.less';

export interface ConditionRowProps {
  /** The current condition. */
  condition: ValidationCondition;
  /** All operator options the user can pick. */
  operators: ValidationOperator[];
  /** Fires with a partial update — e.g. `{ operator }`, `{ value }`, or `{ field, operator, value }`. */
  onChange: (updates: Partial<ValidationCondition>) => void;
  /** Remove button is only rendered when this is provided and not `disabled`. */
  onRemove?: () => void;
  /** Disables field select, operator select, value input, and remove button. */
  disabled?: boolean;
  /**
   * Available fields for the field select column. When omitted the field
   * column is not rendered and each condition inherits its field from context.
   */
  fields?: ValidationField[];
  /**
   * Renders the field column as a permanently-disabled single-option Select
   * showing this field as context. Useful when the field is fixed by the
   * parent (e.g. a per-field validation rule builder in Schema Studio).
   * Takes precedence over `fields` for the disabled display; `renderFieldSelect`
   * still overrides both when provided.
   */
  fixedField?: ValidationField;
  /**
   * Escape hatch — overrides the default DS Select for field selection.
   * Not called when `fields` is empty or undefined.
   */
  renderFieldSelect?: (props: FieldSelectRenderProps) => React.ReactNode;
  /**
   * Escape hatch — overrides the default value input (DS TextInput for single,
   * DS TagInput for multi). Not called when the resolved operator is unary.
   */
  renderValueInput?: (props: ValueInputRenderProps) => React.ReactNode;
  /** Extra class merged onto the root wrapper. */
  className?: string;
}

const DEFAULT_VALUE_SHAPE = 'single' as const;

const resolveValueShape = (op: ValidationOperator | undefined): 'single' | 'multi' =>
  op?.valueShape ?? DEFAULT_VALUE_SHAPE;

const ConditionRow: React.FC<ConditionRowProps> = ({
  condition,
  operators,
  onChange,
  onRemove,
  disabled,
  fields,
  fixedField,
  renderFieldSelect,
  renderValueInput,
  className,
}) => {
  const currentOperator = operators.find((o) => o.value === condition.operator);
  const isUnary = !!currentOperator?.unary;
  const hasFields = (Array.isArray(fields) && fields.length > 0) || !!fixedField;

  const operatorOptions: SelectOptionData[] = React.useMemo(
    () => operators.map((o) => ({ value: o.value, label: o.label })),
    [operators]
  );

  const fieldOptions: SelectOptionData[] = React.useMemo(
    () =>
      fixedField
        ? [{ value: fixedField.value, label: fixedField.label }]
        : (fields ?? []).map((f) => ({ value: f.value, label: f.label })),
    [fields, fixedField]
  );

  const handleFieldChange = React.useCallback(
    (nextField: string) => {
      onChange({ field: nextField, operator: '', value: undefined });
    },
    [onChange]
  );

  const handleOperatorChange = React.useCallback(
    (nextValue: string) => {
      const prevOp = operators.find((o) => o.value === condition.operator);
      const nextOp = operators.find((o) => o.value === nextValue);

      const prevUnary = !!prevOp?.unary;
      const nextUnary = !!nextOp?.unary;
      const prevShape = resolveValueShape(prevOp);
      const nextShape = resolveValueShape(nextOp);

      const shouldClear = prevUnary !== nextUnary || prevShape !== nextShape;
      onChange({ operator: nextValue, ...(shouldClear ? { value: undefined } : {}) });
    },
    [operators, condition.operator, onChange]
  );

  const handleValueChange = React.useCallback(
    (next: string | string[] | undefined) => {
      onChange({ value: next });
    },
    [onChange]
  );

  const fieldCell = hasFields ? (
    renderFieldSelect ? (
      renderFieldSelect({
        condition,
        fields: fields ?? (fixedField ? [fixedField] : []),
        disabled,
        onChange: handleFieldChange,
      })
    ) : (
      <Select
        filterable
        size="small"
        options={fieldOptions}
        value={fixedField ? fixedField.value : condition.field ?? ''}
        disabled={fixedField ? true : disabled}
        placeholder="Select field"
        onChange={handleFieldChange}
      />
    )
  ) : null;

  const renderDefaultValueInput = (): React.ReactNode => {
    if (resolveValueShape(currentOperator) === 'multi') {
      return (
        <TagInput
          size="small"
          value={Array.isArray(condition.value) ? condition.value : []}
          disabled={disabled}
          onChange={(nextValues: string[]) => handleValueChange(nextValues)}
          placeholder="Add values"
        />
      );
    }
    return (
      <TextInput
        size="small"
        value={typeof condition.value === 'string' ? condition.value : ''}
        disabled={disabled}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleValueChange(e.target.value)}
        placeholder="Enter value"
      />
    );
  };

  const valueCell = isUnary
    ? null
    : renderValueInput
    ? renderValueInput({ condition, operator: currentOperator, disabled, onChange: handleValueChange })
    : renderDefaultValueInput();

  const showRemove = !!onRemove && !disabled;

  return (
    <div
      className={cx(
        'ds-condition-row',
        hasFields && 'ds-condition-row--has-field',
        isUnary && 'ds-condition-row--unary',
        className
      )}>
      {hasFields && <div className="ds-condition-row__field">{fieldCell}</div>}
      <div className="ds-condition-row__operator">
        <Select
          filterable
          size="small"
          options={operatorOptions}
          value={condition.operator}
          disabled={disabled}
          placeholder="Select"
          onChange={handleOperatorChange}
        />
      </div>
      <div className={cx('ds-condition-row__value', { 'ds-condition-row__value--hidden': isUnary })}>{valueCell}</div>
      {showRemove && (
        <IconButton
          icon="actionable/delete"
          aria-label="Remove condition"
          variant="tertiary"
          size="medium"
          onClick={onRemove}
        />
      )}
    </div>
  );
};

ConditionRow.displayName = 'ConditionRow';

export { ConditionRow };
export default ConditionRow;
